import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  ExternalLink,
  Download,
  Printer,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Receipt,
  FileText,
  AlertCircle,
  RefreshCw,
  Maximize2
} from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - Vite ?url worker import
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Modal } from '../common/Modal';
import { resolveCloudinaryUrl } from '../../lib/cloudinary';

// Configure matching Vite-bundled PDF.js worker for browser environments
if (typeof window !== 'undefined') {
  try {
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
    }
  } catch (e) {
    console.warn('[ReceiptViewer] PDF worker config notice:', e);
  }
}

interface ReceiptViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  receiptUrl: string | null;
  title?: string;
  invoiceNo?: string;
}

export const ReceiptViewerModal: React.FC<ReceiptViewerModalProps> = ({
  isOpen,
  onClose,
  receiptUrl,
  title = 'Paparan Bukti Resit Bayaran',
  invoiceNo
}) => {
  const [scale, setScale] = useState<number>(1.2);
  const [rotation, setRotation] = useState<number>(0);
  const [isLoadingPdf, setIsLoadingPdf] = useState<boolean>(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<boolean>(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [useCloudinaryImageFallback, setUseCloudinaryImageFallback] = useState<boolean>(false);

  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<any>(null);

  // Normalize any public ID or Cloudinary PDF into a guaranteed 200 OK CDN URL
  const finalUrl = React.useMemo(() => {
    return resolveCloudinaryUrl(receiptUrl);
  }, [receiptUrl]);

  // Determine file type
  const isPdf = Boolean(
    finalUrl &&
    (finalUrl.startsWith('data:application/pdf') ||
      finalUrl.includes('application/pdf') ||
      finalUrl.toLowerCase().endsWith('.pdf') ||
      finalUrl.toLowerCase().includes('.pdf?'))
  );

  const isCloudinaryPdf = Boolean(
    finalUrl &&
    finalUrl.includes('cloudinary.com') &&
    finalUrl.toLowerCase().endsWith('.pdf')
  );

  // Cloudinary image transformation URL for PDF (Page 1 as high-res JPG)
  const cloudinaryJpgUrl = isCloudinaryPdf && finalUrl
    ? finalUrl.replace(/\.pdf$/i, '.jpg')
    : null;

  // Create clean Blob URL when dealing with data: URLs to allow browser opening & downloading
  useEffect(() => {
    setImageError(false);
    if (!finalUrl) {
      setBlobUrl(null);
      return;
    }

    if (finalUrl.startsWith('data:')) {
      try {
        const [header, base64Data] = finalUrl.split(',');
        const mimeMatch = header.match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : (isPdf ? 'application/pdf' : 'image/png');
        const binary = atob(base64Data);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: mime });
        const createdBlobUrl = URL.createObjectURL(blob);
        setBlobUrl(createdBlobUrl);

        return () => {
          URL.revokeObjectURL(createdBlobUrl);
        };
      } catch (err) {
        console.warn('[ReceiptViewer] Failed to create blob from data URL:', err);
      }
    } else {
      setBlobUrl(finalUrl);
    }
  }, [finalUrl, isPdf]);

  // Render PDF pages on canvas when finalUrl changes or scale/rotation changes
  const renderPdfPages = useCallback(async () => {
    if (!finalUrl || !isPdf || useCloudinaryImageFallback) return;

    setIsLoadingPdf(true);
    setPdfError(null);

    try {
      let dataArray: Uint8Array | null = null;

      if (finalUrl.startsWith('data:')) {
        const base64Data = finalUrl.split(',')[1];
        const binary = atob(base64Data);
        dataArray = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          dataArray[i] = binary.charCodeAt(i);
        }
      } else {
        // Remote URL - try fetching arrayBuffer
        try {
          const resp = await fetch(finalUrl);
          if (resp.ok) {
            const buf = await resp.arrayBuffer();
            dataArray = new Uint8Array(buf);
          }
        } catch (fetchErr) {
          console.warn('[ReceiptViewer] Fetch failed (possibly CORS), attempting direct URL load:', fetchErr);
        }
      }

      const loadArgs: any = dataArray
        ? { data: dataArray, useWorkerFetch: false }
        : { url: finalUrl, useWorkerFetch: false };

      const loadingTask = pdfjsLib.getDocument(loadArgs);
      const pdf = await loadingTask.promise;
      pdfDocRef.current = pdf;
      setNumPages(pdf.numPages);

      const container = canvasContainerRef.current;
      if (!container) return;

      // Clear previous canvases
      container.innerHTML = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale, rotation });

        const pageWrapper = document.createElement('div');
        pageWrapper.className = 'relative flex flex-col items-center my-4 first:mt-0 last:mb-0 shadow-lg rounded-xl overflow-hidden border border-slate-200 bg-white';

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.className = 'max-w-full h-auto block';

        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        await page.render({
          canvasContext: ctx,
          viewport
        } as any).promise;

        pageWrapper.appendChild(canvas);

        if (pdf.numPages > 1) {
          const pageLabel = document.createElement('div');
          pageLabel.className = 'py-1 text-[10px] font-bold text-slate-500 bg-slate-50 w-full text-center border-t border-slate-100';
          pageLabel.textContent = `Halaman ${pageNum} daripada ${pdf.numPages}`;
          pageWrapper.appendChild(pageLabel);
        }

        container.appendChild(pageWrapper);
      }
    } catch (err: any) {
      console.warn('[ReceiptViewer] PDF.js rendering error:', err);
      // If it's a Cloudinary PDF and failed, automatically switch to Cloudinary image fallback!
      if (isCloudinaryPdf && cloudinaryJpgUrl) {
        setUseCloudinaryImageFallback(true);
      } else {
        setPdfError(err?.message || 'Gagal memproses paparan PDF.');
      }
    } finally {
      setIsLoadingPdf(false);
    }
  }, [finalUrl, isPdf, scale, rotation, useCloudinaryImageFallback, isCloudinaryPdf, cloudinaryJpgUrl]);

  // Trigger render when modal opens or url changes
  useEffect(() => {
    if (isOpen && finalUrl && isPdf) {
      setUseCloudinaryImageFallback(false);
      setScale(1.2);
      setRotation(0);
      renderPdfPages();
    }
  }, [isOpen, finalUrl, isPdf, renderPdfPages]);

  // Safe Open in New Tab
  const handleOpenOriginal = () => {
    if (!finalUrl) return;

    if (blobUrl) {
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Safe Download
  const handleDownload = () => {
    if (!finalUrl) return;

    const ext = isPdf ? 'pdf' : (finalUrl.includes('.png') ? 'png' : 'jpg');
    const filename = `Resit-${invoiceNo || 'ASMM'}-${Date.now()}.${ext}`;

    const link = document.createElement('a');
    link.href = blobUrl || finalUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print receipt
  const handlePrint = () => {
    if (typeof window === 'undefined') return;

    if (blobUrl) {
      const printWindow = window.open(blobUrl, '_blank');
      if (printWindow) {
        printWindow.focus();
        setTimeout(() => {
          try {
            printWindow.print();
          } catch (_) { }
        }, 800);
        return;
      }
    }
    window.print();
  };

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-4">
        {/* Top Action Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-slate-50 border border-slate-200 rounded-2xl">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 border border-red-200">
              {isPdf ? <FileText className="w-4 h-4" /> : <Receipt className="w-4 h-4" />}
            </div>
            <div className="min-w-0">
              <span className="text-xs font-black text-slate-900 block truncate">
                {invoiceNo ? `Resit Invois: ${invoiceNo}` : 'Salinan Bukti Pembayaran Rasmi'}
              </span>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                {isPdf ? (numPages > 0 ? `Dokumen PDF (${numPages} Halaman)` : 'Dokumen PDF Rasmi') : 'Fail Imej / Slip'}
              </span>
            </div>
          </div>

          {/* Quick Actions Strip */}
          <div className="flex items-center gap-1.5 flex-wrap self-end sm:self-auto">
            {/* Zoom & Rotate Controls */}
            <div className="flex items-center bg-white border border-slate-200 rounded-xl p-0.5 shadow-2xs">
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={scale <= 0.6}
                className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg transition disabled:opacity-40 cursor-pointer"
                title="Kecilkan (Zoom Out)"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="text-[10px] font-mono font-bold px-1.5 text-slate-700 min-w-[42px] text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                type="button"
                onClick={handleZoomIn}
                disabled={scale >= 3.0}
                className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg transition disabled:opacity-40 cursor-pointer"
                title="Besarkan (Zoom In)"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={handleRotate}
                className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg transition cursor-pointer border-l border-slate-100"
                title="Pusing 90° (Rotate)"
              >
                <RotateCw className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Print Button */}
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs rounded-xl border border-slate-200 transition-colors shadow-2xs cursor-pointer"
              title="Cetak Resit"
            >
              <Printer className="w-3.5 h-3.5 text-slate-600" />
              <span className="hidden md:inline">Cetak</span>
            </button>

            {/* Download Button */}
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs rounded-xl border border-slate-200 transition-colors shadow-2xs cursor-pointer"
              title="Muat Turun Fail Resit"
            >
              <Download className="w-3.5 h-3.5 text-slate-600" />
              <span className="hidden md:inline">Muat Turun</span>
            </button>

            {/* Open Original in New Tab */}
            <button
              type="button"
              onClick={handleOpenOriginal}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs rounded-xl transition-colors shadow-2xs cursor-pointer"
              title="Buka fail asal dalam tab baharu"
            >
              <span>Buka Fail</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Content Viewer Body */}
        <div className="bg-slate-900 rounded-2xl overflow-auto border border-slate-800 flex flex-col items-center justify-start min-h-[420px] max-h-[75vh] p-4 relative shadow-inner">
          {/* Loading Spinner */}
          {isLoadingPdf && (
            <div className="absolute inset-0 z-20 bg-slate-900/80 backdrop-blur-xs flex flex-col items-center justify-center text-white gap-3">
              <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
              <span className="text-xs font-bold text-slate-200">
                Memproses paparan dokumen resit...
              </span>
            </div>
          )}

          {/* PDF View (Canvas Rendered via PDF.js) */}
          {isPdf && !useCloudinaryImageFallback && !pdfError && (
            <div
              ref={canvasContainerRef}
              className="w-full flex flex-col items-center justify-center"
            />
          )}

          {/* Cloudinary PDF Transformed to JPG fallback */}
          {isPdf && useCloudinaryImageFallback && cloudinaryJpgUrl && (
            <div className="w-full flex flex-col items-center justify-center">
              <img
                src={cloudinaryJpgUrl}
                alt="Bukti Resit Bayaran"
                style={{
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.2s ease-out'
                }}
                className="max-h-[68vh] w-auto max-w-full object-contain rounded-xl shadow-2xl bg-white"
              />
            </div>
          )}

          {/* Standard Image View (Non-PDF or Cloudinary Transformed JPG) */}
          {!isPdf && finalUrl && !imageError && (
            <div className="w-full h-full flex items-center justify-center min-h-[380px]">
              <img
                src={finalUrl}
                alt="Bukti Resit Bayaran"
                onError={() => setImageError(true)}
                style={{
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.2s ease-out'
                }}
                className="max-h-[68vh] w-auto max-w-full object-contain rounded-xl shadow-2xl bg-white"
              />
            </div>
          )}

          {/* Image Error Fallback */}
          {(!isPdf && imageError) && (
            <div className="w-full max-w-lg p-6 bg-slate-800 rounded-2xl border border-slate-700 text-center space-y-4 my-auto">
              <div className="w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 mx-auto flex items-center justify-center">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-sm font-black text-white">
                  Imej Resit Tidak Dapat Dipaparkan
                </h4>
                <p className="text-xs text-slate-300 mt-1">
                  Fail bukti resit mungkin memerlukan capaian terus. Sila cuba pautan di bawah.
                </p>
              </div>
              <div className="flex justify-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleOpenOriginal}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-bold text-xs rounded-xl flex items-center gap-2 cursor-pointer"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Buka Pautan Fail</span>
                </button>
              </div>
            </div>
          )}

          {/* PDF Error Fallback with Object & Download Button */}
          {pdfError && (
            <div className="w-full max-w-lg p-6 bg-slate-800 rounded-2xl border border-slate-700 text-center space-y-4 my-auto">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 mx-auto flex items-center justify-center">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-sm font-black text-white">
                  Paparan PDF Terhad
                </h4>
                <p className="text-xs text-slate-300 mt-1">
                  Format PDF ini memerlukan aplikasi pembaca dokumen peranti anda. Sila gunakan butang di bawah untuk membuka atau memuat turun salinan penuh.
                </p>
              </div>

              {/* Embedded Object Tag Fallback */}
              {blobUrl && (
                <div className="rounded-xl overflow-hidden border border-slate-700 bg-white">
                  <object
                    data={blobUrl}
                    type="application/pdf"
                    className="w-full h-64"
                  >
                    <p className="p-4 text-xs text-slate-500">
                      Pelayar tidak menyokong paparan PDF terus. Sila muat turun fail di bawah.
                    </p>
                  </object>
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleOpenOriginal}
                  className="w-full sm:w-auto px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs rounded-xl transition shadow-sm flex items-center justify-center gap-2 cursor-pointer"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Buka di Tab Baharu</span>
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="w-full sm:w-auto px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-extrabold text-xs rounded-xl transition border border-slate-600 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>Muat Turun Fail PDF</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="text-[11px] text-slate-500">
            Bukti resit rasmi disahkan dan disimpan dalam arkib perakaunan ASMM.
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs rounded-xl transition-all shadow-sm cursor-pointer"
          >
            Tutup Paparan
          </button>
        </div>
      </div>
    </Modal>
  );
};
